import { Injectable } from '@angular/core';
import Papa, { ParseResult } from 'papaparse';
import * as XLSX from 'xlsx';

export interface ParsedCsv {
  readonly headers: ReadonlyArray<string>;
  readonly rows: ReadonlyArray<ReadonlyArray<string>>;
  /**
   * For CSV: the detected delimiter (`,` or `;`). For XLSX/XLS: the literal string `xlsx`.
   * Downstream mapping does not inspect this field; it is kept for diagnostics only.
   */
  readonly delimiter: string;
}

export class CsvParseError extends Error {
  constructor(message: string, readonly detail?: string) {
    super(message);
    this.name = 'CsvParseError';
  }
}

type SpreadsheetCell = string | number | boolean | Date | null | undefined;

@Injectable({ providedIn: 'root' })
export class CsvParsingService {
  async parseFile(file: File): Promise<ParsedCsv> {
    const extension = this.detectExtension(file.name);
    if (extension === 'xlsx' || extension === 'xls') {
      return this.parseSpreadsheetBuffer(await file.arrayBuffer());
    }
    return this.parseCsvFile(file);
  }

  parseText(text: string): ParsedCsv {
    const result = Papa.parse<string[]>(text, {
      skipEmptyLines: 'greedy',
      dynamicTyping: false
    });
    return this.buildParsedCsv(result);
  }

  normalizeHeader(value: string): string {
    return value
      .trim()
      .replace(/^﻿/, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
  }

  /**
   * Heuristic check for acceptable file extensions, reused by the modal's onFileSelected.
   */
  isSupportedExtension(fileName: string): boolean {
    const ext = this.detectExtension(fileName);
    return ext === 'csv' || ext === 'xlsx' || ext === 'xls';
  }

  private detectExtension(fileName: string): string {
    const match = fileName.toLowerCase().match(/\.([a-z0-9]+)$/);
    return match?.[1] ?? '';
  }

  private parseCsvFile(file: File): Promise<ParsedCsv> {
    return new Promise<ParsedCsv>((resolve, reject) => {
      Papa.parse<string[]>(file, {
        skipEmptyLines: 'greedy',
        dynamicTyping: false,
        encoding: 'utf-8',
        worker: false,
        complete: (result: ParseResult<string[]>) => {
          try {
            resolve(this.buildParsedCsv(result));
          } catch (err) {
            reject(err);
          }
        },
        error: (err: Error) => {
          reject(new CsvParseError('Error parsing CSV file', err.message));
        }
      });
    });
  }

  /**
   * Parses the first sheet of a workbook (xlsx/xls). Exposed as internal so a future
   * caller can pass a pre-loaded buffer (e.g. from drag-n-drop) without going through File.
   */
  private parseSpreadsheetBuffer(buffer: ArrayBuffer): ParsedCsv {
    let workbook: XLSX.WorkBook;
    try {
      workbook = XLSX.read(buffer, { type: 'array' });
    } catch (err) {
      throw new CsvParseError('Error parsing spreadsheet file', this.extractMessage(err));
    }

    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      throw new CsvParseError('Workbook has no sheets');
    }

    const sheet = workbook.Sheets[firstSheetName];
    const rawGrid = XLSX.utils.sheet_to_json<SpreadsheetCell[]>(sheet, {
      header: 1,
      defval: '',
      blankrows: false,
      raw: true,
    });

    if (rawGrid.length === 0) {
      throw new CsvParseError('Spreadsheet is empty');
    }

    const [rawHeaderRow, ...rawRows] = rawGrid;
    const headers = (rawHeaderRow ?? []).map(cell => this.sanitizeCell(cell));
    if (headers.length === 0 || headers.every(h => h.length === 0)) {
      throw new CsvParseError('Spreadsheet header row is empty');
    }

    const rows = rawRows
      .map(row => row.map(cell => this.sanitizeCell(cell)))
      .filter(row => row.some(value => value.length > 0));

    return { headers, rows, delimiter: 'xlsx' };
  }

  private extractMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    return 'Unknown parsing error';
  }

  private buildParsedCsv(result: ParseResult<string[]>): ParsedCsv {
    const data = result.data ?? [];
    if (data.length === 0) {
      throw new CsvParseError('CSV file is empty');
    }

    const [rawHeaderRow, ...rawRows] = data;
    const headers = (rawHeaderRow ?? []).map(value => this.sanitizeCell(value));
    if (headers.length === 0 || headers.every(h => h.length === 0)) {
      throw new CsvParseError('CSV header row is empty');
    }

    const rows = rawRows.map(row => row.map(cell => this.sanitizeCell(cell)));
    const delimiter = result.meta?.delimiter ?? ',';

    return { headers, rows, delimiter };
  }

  private sanitizeCell(value: SpreadsheetCell): string {
    if (value == null) return '';
    if (value instanceof Date) {
      // ISO date without millis; the payload builder normalizes downstream.
      return value.toISOString().split('T')[0];
    }
    return String(value).replace(/^﻿/, '').trim();
  }
}
